import { useRouter } from 'next/router'

import { Filter } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { PRODUCT_FILTER_KEYS } from '@src/quince-site-common/src/constants/product'

import { FilterOption, CheckboxOption, FilterItem } from './filterSection.interface'

/**
 * Maps filter labels to their corresponding filter keys
 */
// getFilterKeyFromLabel function removed

const DEPARTMENT_EXCLUDE_SLUG_LIST = [
  '/men',
  '/women',
  '/women/new-arrivals',
  '/men/new-arrivals',
  '/women/best-sellers',
  '/men/best-sellers',
  '/all/new-arrivals',
]

export function excludeDepartmentFilter(filters: Filter[], slug: string | undefined) {
  if (!slug) return filters
  return DEPARTMENT_EXCLUDE_SLUG_LIST.includes(slug)
    ? filters.filter(filter => filter.name !== (PRODUCT_FILTER_KEYS.departments as string))
    : filters
}

/**
 * Converts a string to title case
 */
export function toTitleCase(str: string) {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Creates checkbox options from filter values
 */
export function createCheckboxOptions(values: (string | FilterOption)[], selectedValues: string[]): CheckboxOption[] {
  return values.map((value, index) => {
    const label = typeof value === 'string' ? value : value.name
    return {
      id: `${label}-${index}`,
      label,
      checked: selectedValues.includes(label),
    }
  })
}

/**
 * Handles navigation to a URL
 */
export async function navigateTo(url: string, isJumpLink = false, router: ReturnType<typeof useRouter>) {
  if (!url) return

  // Handle absolute URLs
  if (url.startsWith('http://') || url.startsWith('https://')) {
    window.location.href = url
    return
  }

  // Use a simple path format
  const formattedUrl = url.startsWith('/') ? url : `/${url}`
  // @TODO: This logic will be removed in the future when productType is multiSelect.
  if (isJumpLink) {
    return router.push(`${formattedUrl}?jumplink=true`)
  } else {
    return router.push(formattedUrl)
  }
}

/**
 * Calculates the total number of selected filters across all filter types
 */
export const calculateTotalSelectedFilters = (selectedFilters: any): number => {
  return Object.values(selectedFilters).reduce((total: number, filterValues) => {
    // Handle undefined, null, or non-array values
    if (!filterValues) return total
    if (!Array.isArray(filterValues)) return total
    return total + filterValues.length
  }, 0)
}

/**
 * Checks if a filter has any selected values
 * @param filter The filter item to check
 * @param selectedFiltersState Map of filter states
 * @returns Boolean indicating if the filter has selected values
 */
export const hasSelectedValues = (filter: FilterItem, selectedFiltersState: Record<string, string[]>) => {
  const state = selectedFiltersState[filter.name]
  return state ? state.length > 0 : false
}

/**
 * Gets the count of selected values for a filter
 * @param filter The filter item to check
 * @param selectedFiltersState Map of filter states
 * @returns Number of selected values
 */
export const getSelectedCount = (filter: FilterItem, selectedFiltersState: Record<string, string[]>) => {
  const state = selectedFiltersState[filter.name]
  return state ? state.length : 0
}
